import React from 'react';
import { Landmark, FileText, CalendarClock } from 'lucide-react';
import { Card, Badge } from './V2Components';
import { expectedNextDueDate } from '../../utils/dateHelpers';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatDate = (d) => {
  if (!d) return null;
  const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const LOAN_TYPE_LABELS = {
  Conventional: 'Conventional',
  FHA: 'FHA',
  VA: 'VA',
  HELOC: 'HELOC',
  Other: 'Other',
};

const MortgageCard = ({ mortgage, onClick }) => {
  const count = mortgage.statement_count || 0;
  // Expected next due: first of the month after the latest statement's due date
  // (mortgage.next_due_date), falling back to the next first-of-month estimate.
  const nextDue = formatDate(expectedNextDueDate(mortgage.next_due_date));
  const typeLabel = LOAN_TYPE_LABELS[mortgage.loan_type] || mortgage.loan_type;

  return (
    <Card
      onClick={onClick}
      className="p-5 cursor-pointer hover:shadow-md transition-shadow"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-xl bg-[var(--bg-hover)] text-[var(--primary)]">
          <Landmark size={24} />
        </div>
        <Badge status={count > 0 ? `${count} ${count === 1 ? 'statement' : 'statements'}` : 'No statements'} />
      </div>

      <div className="font-bold text-[var(--text-main)] truncate" title={mortgage.lender}>
        {mortgage.lender}
      </div>
      <div className="text-xs text-[var(--text-secondary)] mb-3">
        {typeLabel ? <span>{typeLabel}</span> : 'Mortgage'}
        {mortgage.account_last4 && (
          <span className="text-[var(--text-muted)]"> · ••••{mortgage.account_last4}</span>
        )}
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs text-[var(--text-muted)]">Current balance</div>
          <div className="text-xl font-bold text-[var(--primary)]">
            {formatCurrency(mortgage.current_principal_balance)}
          </div>
        </div>
        <div className="text-right">
          {mortgage.last_total_amount_due != null && (
            <div className="text-xs text-[var(--text-secondary)]">
              {formatCurrency(mortgage.last_total_amount_due)}/mo
            </div>
          )}
          <div className="flex items-center justify-end gap-1 text-[var(--text-muted)] mt-1">
            {nextDue && (
              <span className="inline-flex items-center gap-1 text-xs">
                <CalendarClock size={14} /> {nextDue}
              </span>
            )}
            {count > 0 && <FileText size={14} />}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default MortgageCard;
